import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { QuestionsRoot } from "./styles";
import { StepCustomWidget, FreeTextQuestion, MultiQuestion, SingleSelectionQuestion } from "components";
import { connect } from "react-redux";
import { userDataSet } from 'store/userData';
import { useOnlyActiveQuestions } from './hooks';
import { defaultStepPropertyTypeListEnum, questionTypeEnum } from "assets/constants";
import { useFirstRender } from 'hooks';
import { useAnalyticsEventPageView } from 'screens/hooks';
import { addVisibilityToProperties } from 'utils/src/addVisibilityToProperties';
import { checkTypeAndShowAllQuestions, filterPropertyWithoutType } from 'utils';
import { filterMultiSelectQuestions } from './filterMultiSelectQuestions';

const Questions = ({
    step,
    smallScreen,
    activeStep,
    userDataSet,
    userData,
    properties: widgetProperties,
    value: widgetValue,
    setBlockNextButton,
}) => {
    useAnalyticsEventPageView("Questions")

    const ref = useRef();
    const containerRef = useRef();
    const isFirstRender = useFirstRender();
    //indification, if properties is from step, or from widget:

    const properties = useMemo(() =>
        !!step?.properties?.properties
            ? addVisibilityToProperties(step.properties.properties, userData[`questions_${activeStep}`], null, userData)
            : widgetProperties,
        [step, widgetProperties, userData, activeStep]);

    const questions = useMemo(() => userData[`questions_${activeStep}`], [userData, activeStep]);
    const questionsWithNotValidAnswers = useMemo(() => userData[`questions_with_not_valid_answers_${activeStep}`], [userData, activeStep]);
    const showAllQuestions = checkTypeAndShowAllQuestions(properties)
    const onlyActiveQuestions = useOnlyActiveQuestions(properties, questions, activeStep, step, showAllQuestions, questionsWithNotValidAnswers, userData);
    const [scrollToIndex, setScrollToIndex] = useState(0);
    const activeQuestionsFiltered = filterMultiSelectQuestions(filterPropertyWithoutType(onlyActiveQuestions, defaultStepPropertyTypeListEnum.defaultQuestion))
    const prevQuestionsLength = useRef(activeQuestionsFiltered.length);
    useEffect(() => {
        if (activeQuestionsFiltered.length !== prevQuestionsLength.current) {

            isFirstRender
                ? containerRef.current.scrollIntoView(true)
                : ref.current?.scrollIntoView({ behavior: 'smooth', block: "start", alignToTop: true });
            prevQuestionsLength.current = activeQuestionsFiltered.length;
        }
    }, [scrollToIndex, activeQuestionsFiltered.length]);

    const setAnswer = useCallback(
        (answer, index) => {
            userDataSet({
                [`questions_${activeStep}`]: answer,
            });
            const nextIndex = index + 1;
            scrollToIndex < nextIndex && setScrollToIndex(nextIndex);
        },
        [userDataSet, activeStep, scrollToIndex]
    );

    const setNotValidAnswer = useCallback(
        (answer, index) => {
            userDataSet({ [`questions_with_not_valid_answers_${activeStep}`]: answer });
            const nextIndex = index + 1;
            scrollToIndex < nextIndex && setScrollToIndex(nextIndex);
        },
        [userDataSet, activeStep, scrollToIndex]
    );

    const hasWidgetQuestion = !!widgetValue?.question?.questionKey

    return (
        <QuestionsRoot ref={containerRef}>
            {onlyActiveQuestions.map(({ propertyId, type, value }, index) => {

                const isWidgetQuestion = !hasWidgetQuestion
                    || widgetValue?.question?.questionKey === value?.questionKey
                    || widgetValue?.question?.questionKey === value?.question?.questionKey

                if (type === questionTypeEnum.singleSelection && isWidgetQuestion) {
                    return (
                        <SingleSelectionQuestion
                            key={value.questionKey}
                            data={value}
                            userData={userData}
                            questions={questions}
                            setAnswer={(answer) => setAnswer(answer, index)}
                            smallScreen={smallScreen}
                            ref={index === scrollToIndex ? ref : null}
                            propertyId={propertyId}
                            activeStep={activeStep}
                            properties={properties}
                        />)
                }
                if (type === questionTypeEnum.multiSelection && isWidgetQuestion) {
                    return (
                        <MultiQuestion
                            type={type}
                            userData={userData}
                            key={value.questionKey}
                            data={value}
                            ref={index === scrollToIndex ? ref : null}
                            {...{ questions, activeStep, smallScreen }}
                            setAnswer={(answer) => setAnswer(answer, index)}
                            isMultiSelection={true}
                            isFirstRender={isFirstRender}
                            activeStep={activeStep}
                            propertyId={propertyId}
                            properties={properties}
                        />)
                }
                if (type === questionTypeEnum.freeText && isWidgetQuestion) {
                    return (
                        <FreeTextQuestion
                            userData={userData}
                            key={value.questionKey}
                            data={value}
                            ref={index === scrollToIndex ? ref : null}
                            questions={questions}
                            setAnswer={(answer) => setAnswer(answer, index)}
                            setNotValidAnswer={(answer) => setNotValidAnswer(answer, index)}
                            questionsWithNotValidAnswers={questionsWithNotValidAnswers}
                            isFirstRender={isFirstRender}
                            activeStep={activeStep}
                            propertyId={propertyId}
                            properties={properties}
                        />
                    )
                }
                if (!widgetValue?.question?.questionKey) {
                    return <StepCustomWidget ref={index === scrollToIndex ? ref : null} key={index} {...{ propertyId, type, value, index }} />
                }
            }
            )}
        </QuestionsRoot>
    )
}

export default connect(
    ({ current: { steps: { activeStep }, general: { popupState }, userData } }) => ({ userData, activeStep, popupState }),
    { userDataSet })
    (Questions);
